import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Stack, Paper, useTheme, Tooltip } from "@mui/material";
import { Order, OrderStatus, User, AnonymousUser, OrderItem } from "@/types";
import { addMinutes, format } from "date-fns";
import DataCell from "@/components/DataCell";
import DataCard from "../../pages/organization-orders/components/DataCard";
import { getPercentage, multiplyByQuantity } from "@/utils/numbers";
import { useResponsive, useUserProfile, useTerminology } from "@/hooks";
import { nameWithFirstLastnameInitial } from "@/utils/names";
import useOrganizations from "@/features/organizations/useOrganizations";
import { getOrderSubtotal, getOrderTaxes, getOrderTotal } from "@/utils/order";
import { HugeiconsIcon } from "@hugeicons/react";
import { WhatsappIcon } from "@hugeicons/core-free-icons";
import { formatPhone } from "@/utils/phones";
import OrderStages from "./components/OrderStages";
import OrderSummary from "./components/OrderSummary";
import { es } from "date-fns/locale";
import OrderItemsSummary from "./components/OrderItemsSummary";

const OrderDetails = (order: Order) => {
  const { currentOrganization } = useOrganizations();
  const { t } = useTerminology();
  const { palette } = useTheme();
  const { getUserById } = useUserProfile();
  const { isMobile } = useResponsive();
  const location = useLocation();
  const hasStatus = location.pathname.includes("pedidos");

  const contactClient = () => {
    if (order.status === OrderStatus.REJECTED) {
      const message = `Hola ${order?.client.name},%0A%0ASu pedido con número de orden ${order?.number} ha tenido un problema y no podemos procesarlo en este momento.%0A%0ALamentamos los inconvenientes causados.%0A%0AEn un momento nos contactaremos para brindarte más detalles y resolver la situación.%0A%0AGracias por su comprensión.`;
      window.open(`https://wa.me/505${order?.client.phone}?text=${message}`);
    } else {
      const message = `Hola ${order?.client.name},%0A%0ASu pedido con número de orden ${order?.number} está siendo procesado.%0A%0AGracias por su preferencia.`;
      window.open(`https://wa.me/505${order?.client.phone}?text=${message}`);
    }
  };

  const authClient = useMemo(() => getUserById(order?.client as unknown as string) as User, [getUserById, order?.client]);

  const anonClient = order.client;

  const client: AnonymousUser | User = authClient ?? anonClient;

  const subtotal = useMemo(() => {
    if (!order?.items?.length) return 0;

    return order?.items?.map((i) => multiplyByQuantity(i.price, i.quantity)).reduce((acc, curr) => acc + curr, 0);
  }, [order?.items]);

  const taxes = useMemo(() => {
    return getPercentage(subtotal, 15);
  }, [subtotal]);

  const deliveryServiceType = currentOrganization?.settings?.services?.delivery?.type;

  const deliveryValue = useMemo(() => {
    if (order.flow !== "delivery") return null;

    if (order.deliveryFee !== undefined && order.deliveryFee !== null) {
      return order.deliveryFee;
    }

    if (deliveryServiceType === "manual") {
      return "A convenir";
    }

    if (deliveryServiceType === "fixed" && currentOrganization?.settings?.services?.delivery?.fee?.value) {
      return Number(currentOrganization.settings.services.delivery.fee.value);
    }

    return null;
  }, [order.flow, order.deliveryFee, deliveryServiceType, currentOrganization?.settings?.services?.delivery?.fee?.value]);

  const subtotalValue = getOrderSubtotal(order?.items);

  const taxesValue =
    taxes && currentOrganization?.settings?.includeTax
      ? getOrderTaxes(order?.items)
      : null;

  const deliveryFee = typeof order?.deliveryFee === "number" ? order.deliveryFee : (typeof order?.serviceFee === "number" ? order.serviceFee : 0);
  const totalValue = getOrderTotal(order?.items as OrderItem[], currentOrganization?.settings?.includeTax, Number(deliveryFee));

  return (
    <Stack spacing={2} flex={1}>
      {hasStatus ? (
        <Paper>
          <OrderStages order={order} />
        </Paper>
      ) : null}

      <OrderSummary order={order} />

      <OrderItemsSummary
        order={order}
        title={t("product", 2, true)}
        subtotal={subtotalValue}
        deliveryValue={deliveryValue}
        taxesValue={taxesValue}
        total={totalValue}
        isMobile={isMobile}
      />

      <Stack direction={isMobile ? "column" : "row"} spacing={2}>
        {order?.orderedAt ? (
          <DataCard title="General">
            <Stack spacing={1}>
              {order?.branch ? <DataCell label="Sucursal" value={order?.branch?.title} /> : null}
              <DataCell label="Fecha/Hora" value={format(order?.orderedAt, "d MMM, h:mm a")} />
              <DataCell label="Entrega" value={format(addMinutes(order?.orderedAt, 45), "p")} />
            </Stack>
          </DataCard>
        ) : order?.completedAt || order?.archivedAt ? (
          <DataCard title="General">
            <Stack spacing={1}>
              {order?.branch ? <DataCell label="Sucursal" value={order?.branch?.title} /> : null}
              <DataCell label="Fecha/Hora" value={format(order?.completedAt || order?.archivedAt, "dd 'de' MMMM 'de' yyyy, hh:mm a")} />
              <DataCell label="Entrega" value={format(addMinutes(order?.completedAt || order?.archivedAt, 45), "p")} />
              {order?.scheduleTime ? (
                <DataCell
                  label="Hora de entrega"
                  value={format((order?.scheduleTime as any)?.toDate?.() || new Date(order.scheduleTime), "dd 'de' MMMM 'de' yyyy, hh:mm a", {
                    locale: es,
                  })}
                />
              ) : null}
            </Stack>
          </DataCard>
        ) : null}
        <DataCard title="Cliente">
          <Stack spacing={1}>
            {client?.name ? <DataCell label="Nombre" value={nameWithFirstLastnameInitial(client?.name, client?.lastname)} /> : null}
            {client?.phone ? (
              <Stack direction={"row"} spacing={1} alignContent={"center"} justifyContent={"space-between"}>
                <DataCell label="Teléfono" value={formatPhone(client?.phone)} sx={{ flex: 1 }} />
                <Tooltip title="Enviar mensaje" placement="top" arrow>
                  <HugeiconsIcon icon={WhatsappIcon} color={palette.primary.main} size={22} onClick={contactClient} />
                </Tooltip>
              </Stack>
            ) : null}
          </Stack>
        </DataCard>
      </Stack>
    </Stack>
  );
};

export default OrderDetails;
